import React, { useState } from 'react';
import { AlertTriangle, Copy, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

// Warns when a queued physical letter's recipient already received the SAME category (Brief / Break-up),
// or when the same recipient+category sits in the queue more than once. Offers a one-click "remove".
export function LetterDupeBadge({ dup, letterId, onRemoved, compact }) {
  const [busy, setBusy] = useState(false);
  if (!dup || (!dup.already_sent && !dup.queue_twins)) return null;

  const catLabel = dup.category === 'breakup' ? 'Break-up' : 'Brief';
  const sentDate = dup.sent_at ? new Date(dup.sent_at).toLocaleDateString('de-DE') : null;

  const remove = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!window.confirm(`Diesen ${catLabel} als Duplikat aus der Queue entfernen?\n\n${dup.already_sent
      ? `Für diesen Empfänger wurde bereits ein ${catLabel} versendet${sentDate ? ' am ' + sentDate : ''}.`
      : `Derselbe Empfänger hat diesen ${catLabel} mehrfach in der Queue.`}\n\nDer Brief wird ausgeblendet (nicht endgültig gelöscht) und ein offener Druckjob abgebrochen.`)) return;
    setBusy(true);
    try {
      await api.dismissDuplicateLetter(letterId);
      toast.success('Duplikat aus der Queue entfernt');
      onRemoved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Entfernen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`letter-dupe-badge-${letterId}`}>
      {dup.already_sent ? (
        <span data-testid={`letter-dupe-sent-${letterId}`}
          title={`${catLabel} für diesen Empfänger wurde bereits versendet${sentDate ? ' am ' + sentDate : ''}${dup.sent_company ? ' · ' + dup.sent_company : ''}`}
          className="inline-flex items-center gap-1 text-[9px] font-mono font-bold border rounded px-1.5 py-0.5 border-red-500/40 bg-red-500/10 text-red-300">
          <AlertTriangle className="w-3 h-3" /> schon versendet{sentDate ? ` · ${sentDate}` : ''}
        </span>
      ) : (
        <span data-testid={`letter-dupe-queue-${letterId}`}
          title={`${dup.queue_twins + 1}× ${catLabel} für denselben Empfänger in der Queue — entscheide, welcher bleibt`}
          className="inline-flex items-center gap-1 text-[9px] font-mono font-bold border rounded px-1.5 py-0.5 border-amber-500/40 bg-amber-500/10 text-amber-300">
          <Copy className="w-3 h-3" /> {dup.queue_twins + 1}× doppelt
        </span>
      )}
      <button onClick={remove} disabled={busy} data-testid={`letter-dupe-remove-${letterId}`}
        title="Diesen Brief als Duplikat aus der Queue entfernen"
        className="inline-flex items-center gap-0.5 text-[9px] font-mono px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:text-red-300 hover:border-red-500/40 transition-colors disabled:opacity-40">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}{compact ? '' : ' Entfernen'}
      </button>
    </span>
  );
}
